// @flow
import { klona } from "klona/full";

/**
 * Base class for collections of models
 */
class BaseCollection<T> {
  _collection: Array<T>;

  /**
   * Construct base collection
   */
  constructor(initCollection: Array<T> = []) {
    this.collection = initCollection;
  }

  /**
   * Retrieve current collection
   */
  get collection(): Array<T> {
    return this._collection;
  }

  /**
   * Replace current collection with a new collection
   */
  set collection(collection: Array<T>) {
    this._collection = collection;
  }

  /**
   * Add an other collection, array of items or single item to this collection;
   */
  add(items: BaseCollection<T> | Array<T> | T): BaseCollection<T> {
    if (items instanceof BaseCollection) {
      this.collection = [...this.collection, ...items.all];
    } else if (Array.isArray(items)) {
      // $FlowExpectedError[incompatible-type]
      this.collection = [...this.collection, ...items];
    } else {
      this.collection = [...this.collection, items];
    }

    return this;
  }

  /**
   * Remove an item from the collection by it's index.
   */
  removeByIndex(itemIndex: number) {
    if (itemIndex > -1) {
      this.collection = [
        ...this.collection.slice(0, itemIndex),
        ...this.collection.slice(itemIndex + 1),
      ];
    }
  }

  /**
   * Indicates if the collection has items
   */
  get hasItems(): boolean {
    return this.length > 0;
  }

  /**
   * Indicates if collection is empty
   */
  get isEmpty(): boolean {
    return !this.hasItems;
  }

  /**
   * Retrieve all items in collection
   */
  get all(): Array<T> {
    return this.collection;
  }

  /**
   * Get the size of this collection
   */
  get size(): number {
    return this.length;
  }

  /**
   * Get the size of this collection
   */
  get length(): number {
    return this.collection.length;
  }

  /**
   * Return first link in collection
   */
  get first(): T | null {
    return this.collection[0] ?? null;
  }

  /**
   */
  get last(): T | null {
    return this.collection[this.collection.length - 1] ?? null;
  }

  /**
   */
  get(index: number): T | null {
    return this.collection[index] ?? null;
  }

  /**
   * Find item
   */
  find(
    callbackfn: (value: T, index: number, array: Array<T>) => any,
    thisArg?: any,
  ): T | null {
    return this.collection.find(callbackfn, thisArg) ?? null;
  }

  /**
   * Filter items
   */
  filter(
    callbackfn: (value: T, index: number, array: Array<T>) => any,
    thisArg?: any,
  ): Array<T> {
    return this.collection.filter(callbackfn, thisArg);
  }

  /**
   * Check if at least one item occurs
   */
  some(
    callbackfn: (value: T, index: number, array: Array<T>) => any,
    thisArg?: any,
  ): boolean {
    return this.collection.some(callbackfn, thisArg);
  }

  /**
   */
  every(
    callbackfn: (value: T, index: number, array: Array<T>) => any,
    thisArg?: any,
  ): boolean {
    return this.collection.every(callbackfn, thisArg);
  }

  /**
   */
  map<U>(
    callbackfn: (value: T, index: number, array: Array<T>) => U,
    thisArg?: T,
  ): Array<U> {
    return this.collection.map<U, ?T>(callbackfn, thisArg);
  }

  /**
   */
  forEach(
    callbackfn: (value: T, index: number, array: Array<T>) => any,
    thisArg?: any,
  ) {
    this.collection.forEach(callbackfn, thisArg);
  }

  /**
   */
  sort(compareFn?: (a: T, b: T) => number): Array<T> {
    return this.collection.sort(compareFn);
  }

  /**
   * Retrieve an alphabetically sorted array of items
   */
  get sorted(): Array<T> {
    return this.collection.sort();
  }

  /**
   * Returns a clone of the model
   */
  clone(): this {
    return klona(this);
  }
}

export default BaseCollection;
